<template>
    <label :for="inputId" :class="[$style['mds-checkbox'], classObject, sizeClass]" :data-mds-version="version">
        <input
            ref="input"
            v-bind="$attrs"
            type="checkbox"
            :value="value"
            :class="$style['mds-checkbox__input']"
            :disabled="disabled"
            :id="inputId"
            :aria-checked="modelValue"
            v-model="modelValue"
        />
        <span :class="$style['mds-checkbox__visible-wrap']" :data-mds-ref-checked="refChecked">
            <span :class="$style['mds-checkbox__visual']">
                <mds-icon name="check" :class="$style['mds-checkbox__checked-icon']" :size="iconSize" />
                <mds-icon name="minus" :class="$style['mds-checkbox__indeterminate-icon']" :size="iconSize" />
            </span>
            <span :class="$style['mds-checkbox__text']">
                <slot>{{ label }}</slot>

                <span v-if="microcopy || hasMicrocopySlotContent" :class="$style['mds-checkbox__microcopy']">
                    <slot name="mds-microcopy">
                        <span v-if="microcopy" v-html="microcopy"></span>
                    </slot>
                </span>
            </span>
        </span>
    </label>
</template>

<script>
    import MdsIcon from '@mds/icon';
    import { MdsUniqueId } from '@mds/utils-js';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsCheckbox',
        inheritAttrs: false,
        components: {
            MdsIcon,
        },
        model: {
            prop: 'checked',
            event: 'change',
        },
        mixins: [MdsUniqueId],
        data() {
            return {
                version: `${name}-${version}`,
                refChecked: false,
            };
        },
        props: {
            checked: {
                type: [Boolean, Array],
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'Sets the checked attribute of single checkbox or multiple checkboxes as per values passed.',
                    support: ['v-model'],
                },
            },
            hiddenLabel: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, accessibly hides the checkbox label.',
                },
            },
            indeterminate: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sets the indeterminate attribute.',
                },
            },
            microcopy: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the checkbox microcopy.',
                    support: ['v-html'],
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`.',
                    description: 'Sets the size of the checkbox.',
                },
            },
            label: {
                type: String,
                docs: {
                    validation: '**Required**',
                    description: 'Sets the checkbox label.',
                },
            },
            value: {
                type: String,
                default: null,
                docs: {
                    validation: '—',
                    description: 'Sets the checkbox value attribute.',
                },
            },
            readOnly: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, checkbox state cannot be changed by the user.',
                },
            },
        },
        computed: {
            sizeClass() {
                return this.$style[`mds-checkbox--${this.size}`];
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-checkbox--hide-label']] = this.hiddenLabel;
                obj[this.$style['mds-component--on-dark']] = this.onDark;
                obj[this.$style['mds-checkbox--read-only']] = this.readOnly;

                return obj;
            },
            iconSize() {
                return this.size === 'small' || this.size === 'medium' ? 'small' : 'medium';
            },
            modelValue: {
                get() {
                    return this.checked;
                },
                set(value) {
                    this.refChecked = value;
                    this.$emit('change', value);
                },
            },
            hasMicrocopySlotContent() {
                return this.$scopedSlots['mds-microcopy'];
            },
            disabled() {
                return this.readOnly || this.$attrs.disabled;
            },
        },
        watch: {
            indeterminate() {
                this.$refs.input.indeterminate = this.indeterminate;
            },
            checked(val) {
                this.refChecked = val;
            },
        },
        mounted() {
            this.$refs.input.indeterminate = this.indeterminate;
        },
        created() {
            this.inputId = this.generateUuid('mds-checkbox');
        },
    };
</script>

<style lang="scss" module>
    @import './checkbox.scss';
</style>
